import { BlogSection } from '@/types/blog';

export const praxis: BlogSection = {
  id: 'praxis',
  title: 'So arbeiten wir: Musik als Wegbereiter für deine Klarheit',
  content: `
    <p>
      In meinen Coachings beginne ich oft mit Musik. Neulich saß ein Unternehmer vor mir, der seit Wochen zwischen zwei beruflichen Optionen schwankte. Seine Körpersprache war angespannt – verschränkte Arme, flacher Atem, der Blick sprang unruhig umher. Statt direkt in die Analyse zu gehen, startete ich einen instrumentalen Track bei 75 BPM.
    </p>
    
    <p>
      Nach etwa drei Minuten bemerkte ich die Veränderung: Seine Schultern senkten sich, die Atmung wurde tiefer, der Blick ruhiger. Erst dann stellte ich meine erste Frage: „Was würde sich ändern, wenn beide Optionen gleich riskant wären?" Die Antwort kam nicht aus dem Kopf – sie kam aus einer Klarheit, die vorher von emotionalem Lärm überlagert war. Die Musik hatte einen Raum geschaffen, in dem er selbst die Antwort finden konnte.
    </p>
    
    <p>
      Studien bestätigen, was ich in der Praxis beobachte: Musik im 60–120 BPM-Bereich reduziert nachweislich die Aktivität der Amygdala – jener Hirnregion, die für emotionale Stressreaktionen zuständig ist. Gleichzeitig steigt die Aktivität im präfrontalen Cortex, unserem Zentrum für rationales Denken und Planung.
    </p>
    
    <p class="font-semibold mt-6 mb-4">
      Dabei setze ich Musik gezielt als Werkzeug ein:
    </p>
    
    <div class="grid gap-6 my-6">
      <div class="blog-feature-card">
        <div class="blog-feature-icon">
          <svg class="w-6 h-6 text-sage-700 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h4 class="font-semibold text-joerg-900 dark:text-joerg-100 mb-2">Emotionalen Rahmen regulieren</h4>
        <p class="text-joerg-700 dark:text-joerg-300">Die Amygdala – unser emotionales Alarmzentrum – wird durch rhythmische Musik beruhigt. Das schafft neurologischen Raum für klares Denken statt impulsiver Reaktionen.</p>
      </div>
      
      <div class="blog-feature-card">
        <div class="blog-feature-icon">
          <svg class="w-6 h-6 text-sage-700 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
        </div>
        <h4 class="font-semibold text-joerg-900 dark:text-joerg-100 mb-2">Auditive Anker schaffen</h4>
        <p class="text-joerg-700 dark:text-joerg-300">Bestimmte Melodien werden zu verlässlichen Triggern für Gelassenheit. Meine Klienten nutzen „ihre" Tracks später selbstständig – vor wichtigen Gesprächen, Verhandlungen oder Entscheidungen.</p>
      </div>
      
      <div class="blog-feature-card">
        <div class="blog-feature-icon">
          <svg class="w-6 h-6 text-sage-700 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <h4 class="font-semibold text-joerg-900 dark:text-joerg-100 mb-2">Übergang sanft gestalten</h4>
        <p class="text-joerg-700 dark:text-joerg-300">Von emotionaler Aufregung zur rationalen Reflexion ist kein Sprung – es ist ein Gleitflug. Musik wirkt wie eine akustische Brücke, die diesen Übergang ohne Widerstand ermöglicht.</p>
      </div>
    </div>
    
    <p>
      Das Entscheidende: Ich manipuliere nicht. Ich stelle keine suggestiven Fragen. Ich öffne durch gezielte, neutrale Fragen einen Raum – und die Musik sorgt dafür, dass dieser Raum nicht von Stress und Angst besetzt ist, sondern von Klarheit und innerer Ruhe. Die Antworten kommen immer vom Coachee selbst – in seinem Tempo, aus seiner Wahrheit.
    </p>
  `
};
